import React, { useState } from 'react'
import { useTaskQueue, useRunningTasks, useCancelTask } from '../lib/hooks'
import { Button } from '../components/ui/Button'
import { StatusBadge } from '../components/ui/Badge'
import { LoadingSpinner, LoadingCard } from '../components/ui/LoadingSpinner'
import { 
  Clock, 
  Play, 
  Pause, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Trash2,
  RefreshCw
} from 'lucide-react'
import { formatRelativeTime, formatDate } from '../lib/utils'

export const TasksPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'running' | 'queue'>('running')

  // API hooks
  const { data: runningTasks, isLoading: runningLoading } = useRunningTasks()
  const { data: queuedTasks, isLoading: queueLoading } = useTaskQueue()
  const cancelTaskMutation = useCancelTask()

  const handleCancelTask = async (taskId: string) => {
    try {
      await cancelTaskMutation.mutateAsync(taskId)
    } catch (error) {
      // Error handled by mutation
    }
  }

  const getTaskStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <Play className="w-4 h-4 text-blue-600" />
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />
      case 'cancelled':
        return <Pause className="w-4 h-4 text-gray-600" />
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />
    }
  }

  const getTaskStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'bg-blue-100 text-blue-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      case 'cancelled':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = seconds % 60

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`
    } else {
      return `${remainingSeconds}s`
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
          <p className="text-gray-600">Monitor background tasks and processing status</p>
        </div>
        
        <Button
          variant="outline"
          onClick={() => window.location.reload()}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('running')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'running'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Running Tasks ({runningTasks?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('queue')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'queue'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Queued Tasks ({queuedTasks?.length || 0})
          </button>
        </nav>
      </div>

      {/* Tasks Content */}
      <div className="space-y-4">
        {activeTab === 'running' ? (
          runningLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <LoadingCard key={i} />
            ))
          ) : runningTasks?.length === 0 ? (
            <div className="text-center py-12">
              <Play className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No running tasks</h3>
              <p className="text-gray-500">All tasks are currently idle</p>
            </div>
          ) : (
            runningTasks?.map((task) => (
              <TaskCard
                key={task.task_id}
                task={task}
                onCancel={() => handleCancelTask(task.task_id)}
                cancelling={cancelTaskMutation.isLoading}
              />
            ))
          )
        ) : (
          queueLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <LoadingCard key={i} />
            ))
          ) : queuedTasks?.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No queued tasks</h3>
              <p className="text-gray-500">Task queue is empty</p>
            </div>
          ) : (
            queuedTasks?.map((task) => (
              <TaskCard
                key={task.task_id}
                task={task}
                onCancel={() => handleCancelTask(task.task_id)}
                cancelling={cancelTaskMutation.isLoading}
              />
            ))
          )
        )}
      </div>
    </div>
  )
}

// Task Card Component
interface TaskCardProps {
  task: any
  onCancel: () => void
  cancelling: boolean
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onCancel, cancelling }) => {
  const getTaskStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <Play className="w-4 h-4 text-blue-600" />
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />
      case 'cancelled':
        return <Pause className="w-4 h-4 text-gray-600" />
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />
    }
  }

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = seconds % 60

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`
    } else {
      return `${remainingSeconds}s`
    }
  }

  return (
    <div className="card">
      <div className="card-content">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              {getTaskStatusIcon(task.status)}
              <h3 className="text-lg font-semibold text-gray-900">{task.name}</h3>
              <StatusBadge status={task.status} />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Task ID:</strong> {task.task_id}
                </p>
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Started:</strong> {formatDate(task.started_at)}
                </p>
                {task.completed_at && (
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Completed:</strong> {formatDate(task.completed_at)}
                  </p>
                )}
              </div>
              
              <div>
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Progress:</strong> {task.progress}%
                </p>
                {task.processing_time && (
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Processing Time:</strong> {formatDuration(task.processing_time)}
                  </p>
                )}
              </div>
            </div>
            
            {task.progress_message && (
              <div className="mb-4">
                <p className="text-sm text-gray-600">{task.progress_message}</p>
              </div>
            )}
            
            {task.status === 'running' && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Progress</span>
                  <span className="text-sm text-gray-600">{task.progress}%</span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${task.progress}%` }}
                  />
                </div>
              </div>
            )}
            
            {task.error && (
              <div className="mb-4 p-3 bg-red-50 rounded-md">
                <h4 className="text-sm font-medium text-red-700 mb-1">Error</h4>
                <p className="text-sm text-red-600">{task.error}</p>
              </div>
            )}
            
            {task.result && (
              <div className="mb-4 p-3 bg-green-50 rounded-md">
                <h4 className="text-sm font-medium text-green-700 mb-1">Result</h4>
                <pre className="text-sm text-green-600 whitespace-pre-wrap">
                  {JSON.stringify(task.result, null, 2)}
                </pre>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {(task.status === 'running' || task.status === 'pending') && (
              <Button
                variant="destructive"
                size="sm"
                onClick={onCancel}
                loading={cancelling}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Cancel
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TasksPage